import React from 'react';
import { T, useGT } from 'gt-react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import { Grid } from '@/components/common/grid';
import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import ApiError from 'src/api/ApiError';
import DataTable from 'src/md-examples/Tables/DataTable';
import type { ColumnDef } from 'src/lib/tanstack-table';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  lineItem: LineItem;
  isLoading?: boolean;
  error?: ApiError;
}

const DeleteLineItemModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  lineItem,
  isLoading,
  error,
}) => {
  const gt = useGT();

  // Column definitions matching AssociateLineItemModal
  const lineItemColumns: ColumnDef<LineItem>[] = [
    {
      header: gt('SKU', { context: 'sales order line table column header' }),
      accessorKey: 'sku',
      cell: ({ row }) => {
        const item = row.original;
        // Try all possible identifier fields in order of preference
        const identifier = item.customerIdentifier || item.sku || item.upc;

        return identifier ? (
          <MDTypography variant="caption">{identifier}</MDTypography>
        ) : (
          <MDTypography variant="caption" color="text.secondary">
            -
          </MDTypography>
        );
      },
    },
    {
      header: gt('Item', {
        context: 'sales order line table column header',
      }),
      accessorKey: 'itemName',
      cell: ({ row }) => {
        const item = row.original;

        // Try all possible descriptive fields in order of preference
        const description =
          item.customerDescription ||
          item.itemName ||
          item.name ||
          gt('No description available', {
            context: 'placeholder when line item has no description',
          });

        return (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="caption" color="text.secondary">
              {description}
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      header: gt('Quantity', {
        context: 'sales order line table column header',
      }),
      accessorKey: 'quantity',
    },
    {
      header: gt('Unit Price', {
        context: 'sales order line table column header',
      }),
      accessorKey: 'unitPrice',
      cell: ({ row }) => {
        const value = row.original.unitPrice;
        return <>{value ? `$${value.toFixed(2)}` : ''}</>;
      },
    },
    {
      header: gt('Total', {
        context: 'sales order line table column header',
      }),
      accessorKey: 'amount',
      cell: ({ row }) => {
        const original = row.original;
        return (
          <>
            {original.quantity && original.unitPrice
              ? `$${(
                  (original.quantity || 0) * (original.unitPrice || 0)
                ).toFixed(2)}`
              : ''}
          </>
        );
      },
    },
  ];

  const tableData = { columns: lineItemColumns, rows: [lineItem] };

  return (
    <Modal
      open={open}
      title={gt('Delete Line Item', {
        context: 'modal title for deleting a sales order line',
      })}
      closeable
      isLoading={isLoading}
      error={error}
      onCloseButton={{
        text: gt('Cancel', { context: 'modal dismiss button' }),
        onClick: onClose,
        disabled: isLoading,
      }}
      onConfirmButton={{
        text: gt('Delete', { context: 'confirm destructive delete action' }),
        onClick: onConfirm,
        disabled: isLoading,
        color: 'error',
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDTypography variant="body2" mb={3} color="text.secondary">
            <T context="confirmation before deleting a sales order line item">
              Are you sure you want to delete this line item? This action cannot
              be undone.
            </T>
          </MDTypography>
        </Grid>

        <Grid item xs={12}>
          <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
            <T context="section heading in delete line item modal">
              Line Item to Delete
            </T>
          </MDTypography>
          <MDBox
            p={2}
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
          >
            <DataTable<LineItem> table={tableData} />
          </MDBox>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default DeleteLineItemModal;
